import { Link, useParams } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-foreground-500">分类不存在</p>
      </div>
    );
  }

  const pageTitle = `${category.name} - 91次元`;
  const pageDescription = `91次元${category.name}频道，${category.description}。浏览最新${category.name}相关文章，发现精彩内容。`;
  const pageKeywords = `91次元${category.name},${category.name}推荐,二次元${category.name}`;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://91acgn.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91次元',
      url: 'https://91acgn.com',
    },
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://91acgn.com/category/${slug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://91acgn.com/category/${slug}`} />
      <meta property="og:site_name" content="91次元" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <main className="pt-20 pb-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <header className="mt-5 mb-2">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {category.name}
            </h1>
            <p className="text-foreground-500 mt-2">{category.description}</p>
          </header>

          {/* Category Tabs */}
          <div className="mt-8 mb-8 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Articles List */}
          {categoryArticles.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-lg overflow-hidden hover:ring-2 hover:ring-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - 91次元${category.name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full whitespace-nowrap">
                      {article.categoryName}
                    </span>
                  </div>
                  <div className="p-4">
                    <h2 className="font-medium text-foreground-900 line-clamp-2 mb-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span>{article.author}</span>
                        <span>{article.date}</span>
                      </div>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}